import React, { useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";
import { tr } from "../../i18n";
import {
  formatDuration,
  formatPct,
  formatTime,
  formatWeekday,
  splitTimelineDays,
  uptimeLevel,
} from "./statsFormat";

export function StatTile({ label, value, help, icon, tone = "teal" }) {
  return (
    <article className={`stats-tile stats-tile--${tone}`}>
      <div className="stats-tile-head">
        {icon && <span className="stats-tile-icon">{icon}</span>}
        <span className="stats-tile-label">{label}</span>
      </div>
      <div className="stats-tile-value">{value}</div>
      {help && <div className="stats-tile-help">{help}</div>}
    </article>
  );
}

const STATUS = {
  online: { label: "Online", icon: CheckCircleIcon, tone: "good" },
  offline: { label: "Offline", icon: ErrorIcon, tone: "critical" },
  never: { label: "Never connected", icon: HelpOutlineIcon, tone: "none" },
  player_problems: { label: "Player problems", icon: WarningAmberIcon, tone: "warning" },
  self_heal: { label: "Self-heal", icon: RefreshIcon, tone: "warning" },
  load_error: { label: "Load error", icon: WarningAmberIcon, tone: "serious" },
};

/** Status always as icon + text, never color alone. */
export function StatusBadge({ kind }) {
  const s = STATUS[kind] || STATUS.never;
  const Icon = s.icon;
  return (
    <span className={`stats-status stats-status--${s.tone}`} title={tr(s.label)}>
      <Icon fontSize="inherit" aria-hidden="true" />
      <span className="stats-status-text">{tr(s.label)}</span>
    </span>
  );
}

export function UptimeMeter({ pct }) {
  const level = uptimeLevel(pct);
  return (
    <span className="stats-meter" title={pct == null ? tr("No data yet") : formatPct(pct)}>
      <span className={`stats-meter-track stats-meter-track--${level}`}>
        <span className={`stats-meter-fill stats-meter-fill--${level}`} style={{ width: `${pct == null ? 0 : Math.max(2, pct)}%` }} />
      </span>
      <span className="stats-meter-text">{pct == null ? tr("No data yet") : formatPct(pct)}</span>
    </span>
  );
}

const STATE_LABEL = { online: "Online", offline: "Offline", unknown: "No data" };

/** One 24 h bar per day: green online, red offline, grey no data. Opening hours are marked. */
export function DayTimeline({ segments, from, now, window }) {
  const [hover, setHover] = useState(null);
  const rows = splitTimelineDays(segments, from, now);
  const ws = window ? (window.startHour / 24) * 100 : 0;
  const we = window ? (window.endHour / 24) * 100 : 100;
  const describe = (p) =>
    `${tr(STATE_LABEL[p.state])} ${formatTime(p.from)}–${formatTime(p.to)} (${formatDuration(p.to - p.from)})`;
  return (
    <div className="stats-timeline">
      <div className="stats-timeline-legend" aria-hidden="true">
        <span><i className="stats-swatch stats-swatch--online" />{tr("Online")}</span>
        <span><i className="stats-swatch stats-swatch--offline" />{tr("Offline")}</span>
        <span><i className="stats-swatch stats-swatch--unknown" />{tr("No data")}</span>
        {window && (window.startHour > 0 || window.endHour < 24) && (
          <span><i className="stats-swatch stats-swatch--window" />{tr("Opening hours {from}–{to}", { from: String(window.startHour).padStart(2, "0"), to: String(window.endHour).padStart(2, "0") })}</span>
        )}
      </div>
      {rows.map((row) => (
        <div className="stats-timeline-row" key={row.date.getTime()}>
          <span className="stats-timeline-day">{formatWeekday(row.date)}</span>
          <div className="stats-timeline-bar" onMouseLeave={() => setHover(null)}>
            <span className="stats-timeline-window" style={{ left: `${ws}%`, width: `${we - ws}%` }} />
            {row.parts.map((p) => (
              <span
                key={p.from}
                className={`stats-timeline-part stats-timeline-part--${p.state}`}
                style={{ left: `${p.left}%`, width: `${p.width}%` }}
                title={describe(p)}
                onMouseEnter={() => setHover(describe(p))}
              />
            ))}
          </div>
        </div>
      ))}
      <div className="stats-timeline-axis" aria-hidden="true">
        <span />
        <div>
          {[0, 6, 12, 18, 24].map((h) => (
            <span key={h} style={{ left: `${(h / 24) * 100}%` }}>{String(h).padStart(2, "0")}</span>
          ))}
        </div>
      </div>
      <div className="stats-hover-line" aria-live="polite">{hover || " "}</div>
    </div>
  );
}
